import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import * as controllers from 'controllers'
import { PersonDeclarantInformationDto, PersonDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import DeclarantAddEditDialog from './DeclarantAddEditDialog'
import { useAuth } from 'context'

interface DeclarantInformationCardProps {
  person: PersonDto
}

export default function DeclarantInformationCard({ person }: DeclarantInformationCardProps) {
  const { permissions } = useAuth()

  const [declarantAddEditDialogOpen, setDeclarantAddEditDialogOpen] = useState<boolean>(false)
  const [isGettingPersonDeclarantInformation, setIsGettingPersonDeclarantInformation] = useState<boolean>(false)
  const [isSavingPersonDeclarantInformation, setIsSavingPersonDeclarantInformation] = useState<boolean>(false)
  const [personDeclarantInformation, setPersonDeclarantInformation] = useState<PersonDeclarantInformationDto>(
    new PersonDeclarantInformationDto()
  )

  useEffect(() => {
    if (person.personGuid) {
      setIsGettingPersonDeclarantInformation(true)
      controllers
        .getPersonDeclarantInformation(person.personGuid)
        .then(response => {
          setPersonDeclarantInformation(response.value)
        })
        .finally(() => {
          setIsGettingPersonDeclarantInformation(false)
        })
    }
  }, [person])

  return (
    <>
      {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
        <DeclarantAddEditDialog
          initialValues={personDeclarantInformation}
          loading={isSavingPersonDeclarantInformation}
          onClose={() => {
            setDeclarantAddEditDialogOpen(false)
          }}
          onSave={values => {
            setIsSavingPersonDeclarantInformation(true)
            controllers
              .updatePersonDeclarantInformation(person.personGuid!, values)
              .then(response => {
                enqueueSnackbar('Declarant Information Saved Successfully!', { variant: 'success' })
                setPersonDeclarantInformation(response.value)
                setDeclarantAddEditDialogOpen(false)
              })
              .finally(() => {
                setIsSavingPersonDeclarantInformation(false)
              })
          }}
          open={declarantAddEditDialogOpen}
        />
      )}

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Declarant Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
                  <Button
                    endIcon={<Edit />}
                    onClick={() => {
                      setDeclarantAddEditDialogOpen(true)
                    }}
                    variant='text'
                  >
                    EDIT
                  </Button>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingPersonDeclarantInformation && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ mt: 1 }}>
                <Grid item>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingPersonDeclarantInformation && (
              <>
                <Grid item xs={12}>
                  <LabelField label='Declarant Capacity' value={personDeclarantInformation.declarantCapacity} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Signature'
                    onClick={
                      personDeclarantInformation.declarantSignatureFileName
                        ? () => {
                            controllers.getPersonDeclarantSignatureFile(
                              person.personGuid!,
                              personDeclarantInformation.declarantSignatureFileName!
                            )
                          }
                        : undefined
                    }
                    value={personDeclarantInformation.declarantSignatureFileName ?? 'No file uploaded'}
                    error={!personDeclarantInformation.declarantSignatureFileName}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                || This card only shows if person is marked as Declarant. || Only one Declarant can exist. ||
              </ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
